
import { mapState, mapMutations, mapGetters, mapActions } from 'vuex';
import Field from '@/components/ui/field';
import PrimaryButton from '@/components/ui/primaryButton';
import LoginModal from '@/components/loginModal.vue';
import AddressSummary from '@/components/shop/addressSummary.vue';
import SingleSelect from '@/components/ui/singleSelect.vue';

export default {
  name: 'Address',

  components: {
    Field,
    PrimaryButton,
    LoginModal,
    AddressSummary,
    SingleSelect,
  },

  props: {
    allowEdit: {
      type: Boolean,
      default: true,
    },
    billing: {
      type: Boolean,
      default: true,
    },
    sameBilling: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      data: {
        first_name: null,
        last_name: null,
        address: null,
        zip: null,
        city: null,
        phone: null,
        email: null,
        social_security: null,
      },
      isLocked: false,
      error: null,
      errors: {
        first_name: [],
        last_name: [],
        address: [],
        zip: [],
        city: [],
        phone: [],
        email: [],
        social_security: [],
      },
      working: false,
      customerFetched: false,
      message: null,
      loginOpened: false,
      enableTaxNumber: process.env.REGION === 'PT',
    };
  },

  computed: {
    ...mapState([
      'user',
      'orderId',
      'patientId',
      'order',
      'deliveryAddress',
      'checkoutStep',
      'shippingLeft',
      'calculatedZip',
      'regions',
    ]),

    ...mapGetters(['subTotal']),

    saveAddressTitle() {
      return !this.working
        ? this.$t('checkout.saveAddress')
        : this.$t('checkout.savingAddress');
    },

    changeAddressTitle() {
      return !this.working
        ? this.$t('checkout.changeAddress')
        : this.$t('checkout.changingAddress');
    },

    title() {
      return this.billing
        ? this.checkoutStep === 1
          ? this.$t('checkout.enterMailAndZip')
          : this.$t('checkout.billingAddress')
        : this.$t('checkout.delivery');
    },

    billingLock() {
      return this.billing && this.user ? true : this.isLocked;
    },
  },

  watch: {
    user(value) {
      if (value && this.billing) {
        this.getBillingAddress();
        if (this.order && !this.order.address) {
          this.saveAddress();
        }
      } else if (value) {
        this.getDeliveryAddress();
        this.isLocked = false;
      }
      this.$emit('toggleEdit', this.isLocked);
    },
    'data.first_name'(value) {
      if (value) {
        this.errors.first_name = [];
      }
    },
    'data.last_name'(value) {
      if (value) {
        this.errors.last_name = [];
      }
    },
    'data.address'(value) {
      if (value) {
        this.errors.address = [];
      }
    },
    'data.zip'(value) {
      if (value) {
        this.errors.zip = [];
      }
    },
    'data.city'(value) {
      if (value) {
        this.errors.city = [];
      }
    },
    'data.phone'(value) {
      if (value) {
        this.errors.phone = [];
      }
    },
    'data.email'(value) {
      if (value) {
        this.errors.email = [];
      }
    },
    'data.social_security'(value) {
      if (value) {
        this.errors.social_security = [];
      }
    },
    isLocked(value) {
      this.$emit('toggleEdit', value);
    },
  },

  async mounted() {
    if (this.user && this.billing) {
      this.getBillingAddress();
      if (this.order && !this.order.address) {
        this.saveAddress();
      }
    } else if (this.user) {
      this.getDeliveryAddress();
      this.isLocked = false;
    } else if (this.calculatedZip) {
      this.data.zip = this.calculatedZip;
    }
    this.$emit('toggleEdit', this.isLocked);
    if (this.isAngola) {
      const { data: regions } = await this.$bolev.getRegions();
      this.setRegions(regions);
    }
  },

  methods: {
    ...mapMutations([
      'setPatientId',
      'setOrder',
      'setOrderId',
      'setUser',
      'setToken',
      'setLoginMessage',
      'updateDeliveryAddresses',
      'addDeliveryAddress',
      'setCheckoutStep',
      'setShippingLeft',
      'setCalculatedShipping',
      'setCalculatedZip',
      'setRegions',
    ]),

    ...mapActions(['calculateShipping']),

    closeLogin() {
      this.loginOpened = false;
    },

    async getShipping() {
      this.working = true;
      try {
        const { data } = await this.$bolev.getShipping({
          zip: this.data.zip,
          total: this.subTotal,
          email: this.data.email,
          patientId: this.patientId,
        });
        if (data.status === 'customerExistsLoggedOut') {
          this.setLoginMessage(this.$t(`checkout.${data.status}`));
          this.loginOpened = true;
        } else if (data.status === 'customerPhoneExistsLoggedOut') {
          this.working = false;
          this.error = this.$t(`checkout.${data.status}`);
        } else if (!data.shipping || data.shipping.no_sale) {
          this.error = this.$t('checkout.noDelivery');
        } else {
          this.error = null;
          this.setCheckoutStep(2);
          this.setCalculatedZip(this.data.zip);
          this.setCalculatedShipping(data.shipping);
          this.setShippingLeft(data.left);
        }
      } catch (err) {
        if (err.response && err.response.data && err.response.data.errors) {
          this.errors = err.response.data.errors;
        } else if (err.response && err.response.message) {
          this.error = err.response.message;
        } else {
          this.error = err;
        }
      }
      this.working = false;
    },

    enterClick() {
      if (!this.user) {
        this.checkoutStep === 1 ? this.getShipping() : this.saveAddress();
      } else if (this.billing) {
        this.changeAddress();
      } else {
        this.changeDeliveryAddress();
      }
    },

    getDeliveryAddress() {
      this.data.first_name = this.deliveryAddress[0].first_name;
      this.data.last_name = this.deliveryAddress[0].last_name;
      this.data.address = this.deliveryAddress[0].address;
      this.data.zip = this.deliveryAddress[0].zip;
      this.data.city = this.deliveryAddress[0].city;
    },

    getBillingAddress() {
      this.data.first_name = this.user.first_name;
      this.data.last_name = this.user.last_name;
      this.data.address = this.user.address;
      this.data.zip = this.user.zip;
      this.data.city = this.user.city;
      this.data.phone = this.user.phone;
      this.data.email = this.user.email;
      this.data.social_security = this.user.social_security;
      this.isLocked = true;
    },

    async changeDeliveryAddress() {
      try {
        this.working = true;
        const { data } = await this.$bolev.changeDelivery({
          address_id: this.deliveryAddress[0].id,
          first_name: this.data.first_name,
          last_name: this.data.last_name,
          address: this.data.address,
          zip: this.data.zip,
          city: this.data.city,
        });
        const order = await this.$bolev.updateOrder({
          address_id: this.deliveryAddress[0].id,
          orderId: this.orderId,
        });
        this.setOrder(order.data);
        this.calculateShipping(order.data);
        this.updateDeliveryAddresses(data);
        this.isLocked = true;
        this.working = false;
      } catch (err) {
        this.working = false;
        if (err.response && err.response.data && err.response.data.errors) {
          this.errors = err.response.data.errors;
        } else if (err.response && err.response.message) {
          this.error = err.response.message;
        } else {
          this.error = err;
        }
      }
    },

    async changeAddress() {
      try {
        this.working = true;
        const { data } = await this.$bolev.changeAddress({
          orderId: this.sameBilling ? this.orderId : null,
          patientId: this.patientId,
          address: this.data.address,
          zip: this.data.zip,
          city: this.data.city,
          phone: this.data.phone,
          social_security: this.data.social_security,
        });
        if (data.order) {
          this.setOrder(data.order);
          this.calculateShipping(data.order);
        }
        this.isLocked = true;
        this.working = false;
      } catch (err) {
        this.working = false;
        if (err.response && err.response.data && err.response.data.errors) {
          this.errors = err.response.data.errors;
        } else if (err.response && err.response.message) {
          this.error = err.response.message;
        } else {
          this.error = err;
        }
      }
    },

    saveAddress() {
      this.error = null;
      this.message = null;
      this.working = true;
      this.$bolev
        .checkCustomer({
          ...{ orderId: this.orderId, patientId: this.patientId },
          ...this.data,
        })
        .then((res) => {
          if (res.data.status === 'customerExistsLoggedOut') {
            this.working = false;
            this.setLoginMessage(this.$t(`checkout.${res.data.status}`));
            this.loginOpened = true;
          } else if (res.data.status === 'customerPhoneExistsLoggedOut') {
            this.working = false;
            this.error = this.$t(`checkout.${res.data.status}`);
          } else {
            if (res.data.status !== 'customerExistsLoggedIn') {
              this.message = this.$t(`checkout.${res.data.status}`);
            }
            this.customerFetched = true;
            this.working = false;
            this.setOrder(res.data.order);
            this.setOrderId(res.data.order.id);
            if (res.data.status === 'customerCreated') {
              this.setPatientId(res.data.order.patient_id);
              this.setToken(res.data.token);
              this.setUser(res.data);
              this.addDeliveryAddress(res.data.order.address);
            }
          }
        })
        .catch((err) => {
          this.working = false;
          if (err.response && err.response.data && err.response.data.errors) {
            this.errors = err.response.data.errors;
          } else if (err.response && err.response.message) {
            this.error = err.response.message;
          } else {
            this.error = err;
          }
        });
    },
  },
};
